// Gravação que ainda não chegou ao servidor fica guardada NO CELULAR.
//
// A resposta da médica não se repete: se a rede do hospital cair na hora de
// enviar, perder o áudio seria perder a resposta. Então o áudio entra aqui
// ANTES de tentar a rede e só sai quando o servidor confirma — o mesmo princípio
// da fila das medidas (dados/fila.ts). O idCliente vai junto: reenviar depois
// de um envio que chegou mas não respondeu devolve a mesma gravação.

const BASE_API = import.meta.env.VITE_API_URL ?? '/api';

export interface GravacaoPendente {
  idCliente: string;
  roteiroId: string;
  indice: number;
  blob: Blob;
  tipoMime: string;
  duracaoSeg: number;
  gravadoEm: string;
  tentativas: number;
  ultimoErro?: string;
}

const NOME = 'medicov1-gravacoes';
const LOJA = 'pendentes';
let banco: Promise<IDBDatabase> | null = null;
/** Sem IndexedDB (aba anônima antiga), guarda na memória: vale até fechar a página. */
const memoria = new Map<string, GravacaoPendente>();

function abrir(): Promise<IDBDatabase> | null {
  if (typeof indexedDB === 'undefined') return null;
  banco ??= new Promise((ok, falhou) => {
    const req = indexedDB.open(NOME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(LOJA, { keyPath: 'idCliente' }); };
    req.onsuccess = () => ok(req.result);
    req.onerror = () => falhou(req.error);
  });
  return banco;
}

async function naLoja<T>(modo: IDBTransactionMode, fazer: (l: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const b = await abrir()!;
  return new Promise((ok, falhou) => {
    const req = fazer(b.transaction(LOJA, modo).objectStore(LOJA));
    req.onsuccess = () => ok(req.result);
    req.onerror = () => falhou(req.error);
  });
}

export async function guardarPendente(g: GravacaoPendente): Promise<void> {
  try { if (abrir()) { await naLoja('readwrite', (l) => l.put(g)); return; } } catch { /* cai para a memória */ }
  memoria.set(g.idCliente, g);
}

export async function removerPendente(idCliente: string): Promise<void> {
  memoria.delete(idCliente);
  try { if (abrir()) await naLoja('readwrite', (l) => l.delete(idCliente)); } catch { /* nada a fazer */ }
}

export async function listarPendentes(roteiroId?: string): Promise<GravacaoPendente[]> {
  let todas: GravacaoPendente[] = [...memoria.values()];
  try { if (abrir()) todas = [...todas, ...(await naLoja('readonly', (l) => l.getAll() as IDBRequest<GravacaoPendente[]>))]; } catch { /* só a memória */ }
  const unicas = [...new Map(todas.map((g) => [g.idCliente, g])).values()];
  return unicas
    .filter((g) => !roteiroId || g.roteiroId === roteiroId)
    .sort((a, b) => a.gravadoEm.localeCompare(b.gravadoEm));
}

const EXTENSAO: Record<string, string> = { 'audio/mp4': 'm4a', 'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mpeg': 'mp3', 'audio/wav': 'wav' };

/** Envia uma; se o servidor confirmar (201 ou 200 de reenvio), tira da fila. */
export async function enviarPendente(g: GravacaoPendente): Promise<boolean> {
  const corpo = new FormData();
  // Os campos ANTES do arquivo: o servidor lê o que veio antes dele.
  corpo.append('idCliente', g.idCliente);
  corpo.append('indice', String(g.indice));
  corpo.append('duracaoSeg', String(g.duracaoSeg));
  corpo.append('gravadoEm', g.gravadoEm);
  corpo.append('arquivo', g.blob, `resposta.${EXTENSAO[g.tipoMime] ?? 'm4a'}`);
  try {
    const r = await fetch(`${BASE_API}/roteiros/${g.roteiroId}/gravacoes`, { method: 'POST', body: corpo, credentials: 'include' });
    if (r.ok) { await removerPendente(g.idCliente); return true; }
    const json = await r.json().catch(() => null) as { message?: string } | null;
    // 4xx que não seja de rede/sessão não melhora tentando de novo — mas o áudio
    // continua guardado, e a tela mostra o motivo.
    await guardarPendente({ ...g, tentativas: g.tentativas + 1, ultimoErro: json?.message ?? `erro ${r.status}` });
  } catch {
    await guardarPendente({ ...g, tentativas: g.tentativas + 1, ultimoErro: 'sem internet — tento de novo sozinho' });
  }
  return false;
}

/** Tenta enviar tudo o que está guardado (deste roteiro, ou de todos). */
export async function enviarTodas(roteiroId?: string): Promise<number> {
  let enviadas = 0;
  for (const g of await listarPendentes(roteiroId)) if (await enviarPendente(g)) enviadas++;
  return enviadas;
}
